import "@/App.css";
import "@/theme.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { ThemeProvider } from "@/context/ThemeContext";
import AddToHomeScreen from "@/components/AddToHomeScreen";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import History from "@/pages/History";
import InsightsTimeline from "@/pages/InsightsTimeline";
import Library from "@/pages/Library";
import Calculator from "@/pages/Calculator";
import Calculators from "@/pages/Calculators";
import Inventory from "@/pages/Inventory";
import Cycles from "@/pages/Cycles";
import Profile from "@/pages/Profile";
import Weight from "@/pages/Weight";
import Nutrition from "@/pages/Nutrition";
import Supplements from "@/pages/Supplements";
import PublicCoachPage from "@/pages/PublicCoachPage";
import MyClients from "@/pages/MyClients";
import Coaches from "@/pages/Coaches";
import Groups from "@/pages/Groups";
import GroupDetail from "@/pages/GroupDetail";
import GroupJoin from "@/pages/GroupJoin";
import AdminGroups from "@/pages/AdminGroups";
import AdminSettings from "@/pages/AdminSettings";
import AdminUsers from "@/pages/AdminUsers";
import AdminSupporters from "@/pages/AdminSupporters";
import AdminModeration from "@/pages/AdminModeration";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminSupportPage from "@/pages/AdminSupportPage";
import AdminSecurity from "@/pages/AdminSecurity";
import Messages from "@/pages/Messages";
import { CommunityList, CommunityDetail } from "@/pages/Community";
import Playbook from "@/pages/Playbook";
import PlaybookDetail from "@/pages/PlaybookDetail";
import Maintenance from "@/pages/Maintenance";
import ThemeStudio from "@/pages/ThemeStudio";
import Tutorial from "@/pages/Tutorial";
import PublicProfile from "@/pages/PublicProfile";
import Feed from "@/pages/Feed";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Support from "@/pages/Support";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
        <AuthProvider>
          <SettingsProvider>
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/coach/:handle" element={<PublicCoachPage />} />
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/calculator" element={<ProtectedRoute><Calculator /></ProtectedRoute>} />
              <Route path="/calculators" element={<ProtectedRoute><Calculators /></ProtectedRoute>} />
              <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
              <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
              <Route path="/cycles" element={<ProtectedRoute><Cycles /></ProtectedRoute>} />
              <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
              <Route path="/insights/timeline" element={<ProtectedRoute><InsightsTimeline /></ProtectedRoute>} />
              <Route path="/tutorial" element={<ProtectedRoute><Tutorial /></ProtectedRoute>} />
              <Route path="/u/:userId" element={<PublicProfile />} />
              <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
              <Route path="/weight" element={<ProtectedRoute><Weight /></ProtectedRoute>} />
              <Route path="/nutrition" element={<ProtectedRoute><Nutrition /></ProtectedRoute>} />
              <Route path="/supplements" element={<ProtectedRoute><Supplements /></ProtectedRoute>} />
              <Route path="/clients" element={<ProtectedRoute><MyClients /></ProtectedRoute>} />
              <Route path="/coaches" element={<ProtectedRoute><Coaches /></ProtectedRoute>} />
              <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
              <Route path="/groups/join/:token" element={<ProtectedRoute><GroupJoin /></ProtectedRoute>} />
              <Route path="/groups/:id" element={<ProtectedRoute><GroupDetail /></ProtectedRoute>} />
              <Route path="/admin/groups" element={<ProtectedRoute adminOnly><AdminGroups /></ProtectedRoute>} />
              <Route path="/community" element={<ProtectedRoute><CommunityList /></ProtectedRoute>} />
              <Route path="/community/:id" element={<ProtectedRoute><CommunityDetail /></ProtectedRoute>} />
              {/* Legacy alias so old email/link URLs like /community/topic/<id> still work. */}
              <Route path="/community/topic/:id" element={<ProtectedRoute><CommunityDetail /></ProtectedRoute>} />
              <Route path="/playbook" element={<ProtectedRoute><Playbook /></ProtectedRoute>} />
              <Route path="/playbook/:id" element={<ProtectedRoute><PlaybookDetail /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/admin/peptides" element={<ProtectedRoute adminOnly><Library /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
              <Route path="/admin/moderation" element={<ProtectedRoute staffOnly><AdminModeration /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute staffOnly><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin/supporters" element={<ProtectedRoute staffOnly><AdminSupporters /></ProtectedRoute>} />
              <Route path="/admin/feedback" element={<ProtectedRoute adminOnly><AdminFeedback /></ProtectedRoute>} />
              <Route path="/admin/support-page" element={<ProtectedRoute adminOnly><AdminSupportPage /></ProtectedRoute>} />
              <Route path="/admin/security" element={<ProtectedRoute adminOnly><AdminSecurity /></ProtectedRoute>} />
              <Route path="/admin/theme" element={<ProtectedRoute adminOnly><ThemeStudio /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster
              theme="system"
              position="bottom-right"
              richColors
              closeButton
            />
            <AddToHomeScreen />
          </SettingsProvider>
        </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
